import React, {Component} from 'react';
import {isEmpty, getToken, getRefreshToken} from '../../../../../../utils/helpers';
import moment from "moment";
import {showModal} from "../../../../../../actions/modal.actions";
import {connect} from "react-redux";
import {getUserPermission, getAccountDetails, createLoadingSelector} from "../../../../../../selectors";
import DiagnosticIndicator, { DiagnosticStatus, mapStatus } from "src/components/UI/DiagnosticIndicator";
import {API_ROOT} from "../../../../../../utils/api-config";
import {getDiscounts} from "../../../../../../actions/accountDetailsActions/billing.actions";
import Loader from "../../../../../../components/Loader";

export class ServiceSummary extends Component {

    state = {
        isExpanded: null,
        diagnostics: {},
        loadingDiagnostics: {},
        deviceStatuses: {},
        deviceLogs: {},
    }


    showPackageFeatures = () => {
        this.setState({isExpanded: !this.state.isExpanded});
    }

    fetchDiagnostics = async (deviceProfileId) => {
        this.setState(prevState => ({
            loadingDiagnostics: {
                ...prevState.loadingDiagnostics,
                [deviceProfileId]: true
            }
        }));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
            const response = await fetch(`${API_ROOT}/provisioning/diagnostics?deviceProfileId=${deviceProfileId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Token': getToken(),
                    'X-API-Refresh-Token': getRefreshToken()
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const diagnosticData = await response.json();

            this.setState(prevState => ({
                diagnostics: {
                    ...prevState.diagnostics,
                    [deviceProfileId]: diagnosticData
                },
                loadingDiagnostics: {
                    ...prevState.loadingDiagnostics,
                    [deviceProfileId]: false
                }
            }));

            this.autoRunDeviceStatus(deviceProfileId, diagnosticData);

        } catch (error) {
            clearTimeout(timeoutId);
            this.setState(prevState => ({
                loadingDiagnostics: {
                    ...prevState.loadingDiagnostics,
                    [deviceProfileId]: false
                }
            }));
        }
    }

    autoRunDeviceStatus = async (deviceProfileId, diagnosticData) => {
        const deviceStatusOperation = diagnosticData?.find(diag => diag.operationName === 'device-status');

        if (!deviceStatusOperation) {
            return;
        }

        const {service} = this.props;
        const device = service?.serviceDevices?.find(d => d.deviceProfileId === deviceProfileId);

        if (!device) {
            return;
        }


        await this.runDiagnosticAction('device-status', device, deviceStatusOperation);
    }

    runDiagnosticAction = async (actionKey, device, diagnosticResponse) => {
        const deviceId = device.id || `${device.deviceProfileId}-${device.serialNumber}`;

        const actionController = new AbortController();
        const actionTimeoutId = setTimeout(() => actionController.abort(), 30000);

        try {
            this.setState(prevState => ({
                deviceStatuses: {
                    ...prevState.deviceStatuses,
                    [deviceId]: DiagnosticStatus.LOADING
                }
            }));

            const payload = {
                deviceNums: device?.apiServiceDeviceNums || [],
                deviceProfile: diagnosticResponse?.deviceProfileName || "ONT",
                interfaces: [diagnosticResponse?.provisioningInterfaceName || "CALIX-SMX"]
            };

            const response = await fetch(
                `${API_ROOT}/provisioning/diagnostic/${actionKey}`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-API-Token': getToken(),
                        'X-API-Refresh-Token': getRefreshToken()
                    },
                    body: JSON.stringify(payload),
                    signal: actionController.signal
                }
            );
            clearTimeout(actionTimeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const actionResponseData = await response.json();

            if (actionResponseData && actionResponseData.state) {
                const newStatus = mapStatus(actionResponseData);

                let parsedLogs = actionResponseData.rawResponse;
                if (typeof parsedLogs === 'string') {
                    try {
                        const jsonString = parsedLogs.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false');
                        parsedLogs = JSON.parse(jsonString);
                    } catch (parseError) {
                        console.warn('Failed to parse rawResponse string:', parseError);
                        parsedLogs = { error: 'Failed to parse response', raw: parsedLogs };
                    }
                }

                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: newStatus
                    },
                    deviceLogs: {
                        ...prevState.deviceLogs,
                        [deviceId]: parsedLogs
                    }
                }));
            } else {
                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: DiagnosticStatus.UNKNOWN
                    }
                }));
            }

        } catch (error) {
            clearTimeout(actionTimeoutId);
            this.setState(prevState => ({
                deviceStatuses: {
                    ...prevState.deviceStatuses,
                    [deviceId]: DiagnosticStatus.UNKNOWN
                }
            }));
        }
    }

    showLogsModal = (device, deviceId) => {
        const deviceLogs = this.state.deviceLogs[deviceId];
        const deviceStatus = this.state.deviceStatuses[deviceId];
        const deviceProfileId = device.deviceProfileId;
        const diagnosticResponse = this.state.diagnostics[deviceProfileId];
        const deviceStatusDiagnostic = diagnosticResponse?.find(diag => diag.operationName === 'device-status');

        this.props.showModal('EQUIPMENT_DEVICE_STATUS_LOGS_MODAL', {
            device: device,
            logs: deviceLogs,
            status: deviceStatus,
            diagnosticResponse: deviceStatusDiagnostic, // Keep for backward compatibility
            diagnostics: diagnosticResponse, // Pass all available diagnostics
            deviceId: deviceId, // Pass deviceId so modal can track updates
            onUpdateParent: (updatedStatus, updatedLogs) => {
                // Update ServiceSummary state when modal makes changes
                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: updatedStatus
                    },
                    deviceLogs: {
                        ...prevState.deviceLogs,
                        [deviceId]: updatedLogs
                    }
                }));
            }
        });
    }

    componentDidMount() {
        const {service, accountId} = this.props;

        if (accountId) {
            this.props.getDiscounts(accountId);
        }

        if (service && service.serviceDevices) {
            service.serviceDevices.forEach(device => {
                if (device.deviceProfileId) {
                    this.fetchDiagnostics(device.deviceProfileId);
                }
            });
        }
    }




    render() {

        const {isExpanded, diagnostics, loadingDiagnostics, deviceStatuses, deviceLogs} = this.state;

        const {service, showHistoricalData, canEditEquipment, canEditServiceLineNumber, canAddServicelineDiscount, canEditServicelineDiscount, discountsData, discountsLoading} = this.props;

        if (isEmpty(service)) {
            return null;
        }

        let recurringFeaturePackageFees = null;
        let recurringFeatureFees = null;
        let oneTimeFeaturePackageFees = null;
        let oneTimeFeatureFees = null;

        if (!showHistoricalData) {
            recurringFeaturePackageFees = !isEmpty(service.activeFeaturePackages) ?
                service.activeFeaturePackages.filter((x) => x.featureType !== 'ACTIVATION') : null;

            recurringFeatureFees = !isEmpty(service.servicelineFeatures) ?
                service.servicelineFeatures.filter((x) => x.featureType !== 'ACTIVATION') : null;

            oneTimeFeaturePackageFees = !isEmpty(service.activeFeaturePackages) ?
                service.activeFeaturePackages.filter((x) => x.featureType === 'ACTIVATION') : null;

            oneTimeFeatureFees = !isEmpty(service.servicelineFeatures) ?
                service.servicelineFeatures.filter((x) => x.featureType === 'ACTIVATION') : null;
        }
        else {

            recurringFeatureFees = !isEmpty(service.servicelineFeatures) ?
                service.servicelineFeatures.filter((x) => x.featureType !== 'ACTIVATION') : null;

            oneTimeFeatureFees = !isEmpty(service.servicelineFeatures) ?
                service.servicelineFeatures.filter((x) => x.featureType === 'ACTIVATION') : null;
        }

        return (
            <div className="services-summary mt-0">
                <div className="services-summary-body">
                    <div className="plan-wrapper">
                        <div className="serviceline-wrapper">

                            {service.servicelineNums &&
                            <div className="plan-section">
                                <h5>Numbers</h5>
                                {!isEmpty(service.servicelineNums) && service.servicelineNums.map((num) =>
                                    <div className="package-wrapper">
                                        <div className="package-option">
                                            <span className="po-title">{num.number}</span>
                                            <span className="po-price" style={{ marginRight: '8px' }}>{num.type}</span>
                                            {canEditServiceLineNumber && !num.endDate &&
                                                <button
                                                    title="Edit Service Line Number"
                                                    onClick={() =>
                                                        this.props.showModal('EDIT_SERVICE_LINE_NUMBER_MODAL', {
                                                            serviceLineId: service.serviceLineId,
                                                            serviceLineNumId: num.id,
                                                            currentNumber: num.number,
                                                            showHistoricalData,
                                                        })
                                                    }
                                                    className="btn btn-xs btn-outline-secondary btn-cn-edit"
                                                >
                                                    Edit
                                                </button>
                                            }
                                            {showHistoricalData &&
                                            <div className="po-date">
                                                {num.startDate && moment(num.startDate).format("MM/DD/YYYY")}
                                                {" - "}
                                                {num.endDate ? moment(num.endDate).format("MM/DD/YYYY") : "Now"}
                                            </div>
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                            }

                            {!isEmpty(recurringFeaturePackageFees) || !isEmpty(recurringFeatureFees) ?
                            <div className="plan-section">

                                <h5>
                                    Recurring
                                    <button
                                        className="btn btn-xs btn-secondary"
                                        onClick={this.showPackageFeatures}
                                    >
                                        <i className="fa fa-info" />
                                    </button>
                                </h5>

                                {!isEmpty(recurringFeaturePackageFees) && recurringFeaturePackageFees.map((feature, index) => (
                                <div className="package-wrapper" key={index}>
                                    <div className="package-option">
                                        <span className="po-title">
                                            {feature.description &&
                                            `${feature.description.replace(/\s<br><(\w+)\b[^<>]*>[\s\S]*?<\/\1>/g, '')}:`}{' '}
                                            {feature.featurePackageOptions.map((featureAndPackageOptions) => {
                                                return featureAndPackageOptions.description
                                            })}
                                        </span>
                                        <span className="po-price">
                                            ${feature.featurePackageOptions[0].recurringMonthlyPrice
                                                .toFixed(2)
                                                .replace(/\d(?=(\d{3})+\.)/g, '$&,')}/mo
                                        </span>

                                        {isExpanded &&
                                        <div className="container-list-bubble container-list-bubble-features">
                                            {feature.featurePackageOptions.map((options) => options.features.map((f) =>
                                            <dl>
                                                <dt>{f.featureDescription}</dt>
                                                <dd>{f.qty} &times; ${f.recurringMonthlyPrice}/mo</dd>
                                            </dl>
                                            ))}
                                        </div>
                                        }

                                    </div>
                                </div>
                                ))}

                                {!isEmpty(recurringFeatureFees) && recurringFeatureFees.map((feature, index) => (
                                <div className="package-wrapper" key={index}>
                                    <div className="package-option">
                                        <span className="po-title">
                                            {feature.featureDescription && feature.featureDescription}
                                        </span>
                                        <span className="po-price">
                                            {feature.qty > 1
                                                ? <>Qty: {feature.qty}<span className="text-muted"> | </span></>
                                                : ""}
                                            ${feature.recurringMonthlyPrice
                                                .toFixed(2)
                                                .replace(/\d(?=(\d{3})+\.)/g, '$&,')}/mo
                                        </span>
                                        {showHistoricalData &&
                                        <div className="po-date">
                                            {feature.startDate && moment(feature.startDate).format("MM/DD/YYYY")}
                                            {" - "}
                                            {feature.endDate ? moment(feature.endDate).format("MM/DD/YYYY") : "Now"}
                                        </div>
                                        }
                                    </div>
                                </div>
                                ))}

                            </div>
                            :
                            null
                            }

                            <div className="plan-section">
                                <h5>
                                    Discounts
                                    {canAddServicelineDiscount && (
                                        <button
                                            className="btn btn-xs btn-outline-secondary btn-cn-edit"
                                            title="Add Discount"
                                            onClick={() => this.props.showModal('ADD_ACCOUNT_DISCOUNT_MODAL', {
                                                type: 'create',
                                                serviceLineId: service.serviceLineId,
                                                onCloseCallback: () => this.props.getDiscounts(this.props.accountId),
                                            })}
                                        >
                                            Add
                                        </button>
                                    )}
                                </h5>
                                {discountsLoading ? (
                                    <Loader />
                                ) : (() => {
                                    const today = moment().format('YYYY-MM-DD');
                    const serviceDiscounts = discountsData.filter(d => {
                        if (d.serviceLineNum !== service.number) return false;
                        if (showHistoricalData) return true;
                        return moment.utc(d.validFrom).format('YYYY-MM-DD') <= today && moment.utc(d.validTo).format('YYYY-MM-DD') > today;
                    });
                                    return isEmpty(serviceDiscounts) ? (
                                        <div className="package-wrapper">
                                            <div className="package-option">
                                                <span className="po-title text-muted">No discounts</span>
                                            </div>
                                        </div>
                                    ) : (
                                    serviceDiscounts.map((discount, index) => (
                                        <div className="package-wrapper" key={index}>
                                            <div className="package-option">
                                                <span className="po-title">{discount.description}</span>
                                                <span className="po-price">
                                                    {(() => {
                                                        const parts = [];
                                                        if (discount.amount != null && discount.amount !== 0) {
                                                            parts.push(`$${discount.amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`);
                                                        }
                                                        if (discount.percentage != null && discount.percentage !== 0) {
                                                            parts.push(`${discount.percentage}%`);
                                                        }
                                                        return parts.length > 0 ? parts.join(' + ') : null;
                                                    })()}
                                                </span>
                                                {canEditServicelineDiscount && (
                                                    <button
                                                        title="Edit Discount"
                                                        className="btn btn-xs btn-cn-edit"
                                                        onClick={() => this.props.showModal('ADD_ACCOUNT_DISCOUNT_MODAL', {
                                                            type: 'edit',
                                                            row: {
                                                                id: discount.id,
                                                                validFrom: discount.validFrom || '',
                                                                validTo: discount.validTo || '',
                                                                discountDescription: discount.description,
                                                                serviceLineNumber: service.number,
                                                            },
                                                            onCloseCallback: () => this.props.getDiscounts(this.props.accountId),
                                                        })}
                                                    >
                                                    </button>
                                                )}
                                                {(discount.validFrom || discount.validTo) && (
                                                    <div className="po-date">
                                                        {discount.validFrom ? moment.utc(discount.validFrom).format('MM/DD/YYYY') : ''}
                                                        {' - '}
                                                        {discount.validTo ? moment.utc(discount.validTo).format('MM/DD/YYYY') : 'No end date'}
                                                    </div>
                                                )}
                                                {!isEmpty(discount.features) && (
                                                    <div className="po-date">
                                                        {Array.isArray(discount.features) ? discount.features.join(', ') : discount.features}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                    );
                                })()}
                            </div>

                            {!isEmpty(oneTimeFeaturePackageFees) || !isEmpty(oneTimeFeatureFees) ? (
                            <div className="plan-section">

                                <h5>Onetime</h5>

                                {!isEmpty(oneTimeFeaturePackageFees) && oneTimeFeaturePackageFees .map((feature, index) => (
                                <div className="package-wrapper" key={index}>
                                    <div className="package-option package-option-mandatory">
                                        <span className="po-title">
                                            {feature.description && `${feature.description}:`}{' '}
                                            {feature.note}
                                        </span>
                                        <span className="po-price">
                                            ${feature.featurePackageOptions[0].oneTimePrice
                                                .toFixed(2)
                                                .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                        </span>
                                    </div>
                                </div>
                                ))}

                                {!isEmpty(oneTimeFeatureFees) && oneTimeFeatureFees.map((feature, index) => (
                                <div className="package-wrapper" key={index}>
                                    <div className="package-option package-option-mandatory">
                                        <span className="po-title">
                                            {feature.featureDescription && feature.featureDescription}
                                        </span>
                                        <span className="po-price">
                                            {feature.qty > 1
                                                ? <>Qty: {feature.qty}<span className="text-muted"> | </span></>
                                                : ""}
                                            ${feature.oneTimePrice
                                                .toFixed(2)
                                                .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                        </span>
                                        {showHistoricalData &&
                                        <div className="po-date">
                                            {feature.startDate && moment(feature.startDate).format("MM/DD/YYYY")}
                                            {" - "}
                                            {feature.endDate ? moment(feature.endDate).format("MM/DD/YYYY") : "Now"}
                                        </div>
                                        }
                                    </div>
                                </div>
                                ))}

                            </div>
                            ) : null }

                            {!isEmpty(service.serviceDevices) &&
                            <div className="plan-section">

                                <h5>Equipment</h5>

                                {service.serviceDevices.map((device, index) => {
                                    const deviceProfileId = device.deviceProfileId;
                                    const diagnosticResponse = diagnostics[deviceProfileId];
                                    const isLoading = loadingDiagnostics[deviceProfileId];
                                    const hasDeviceDiagnostics = diagnosticResponse && Array.isArray(diagnosticResponse) && diagnosticResponse.length > 0;

                                    const availableDiagnostics = hasDeviceDiagnostics ? diagnosticResponse : null;

                                    const deviceStatusDiagnostic = hasDeviceDiagnostics
                                        ? diagnosticResponse.find(diag => diag.operationName === 'device-status')
                                        : null;

                                    return (
                                <div className="package-option" key={index}>
                                    <span className="po-title">{device.deviceItemDescription ? device.deviceItemDescription : null}</span>
                                    {canEditEquipment &&
                                        <button
                                            title="Edit"
                                            onClick={() =>
                                                this.props.showModal('EDIT_EQUIPMENT_MODAL', {
                                                    device: device,
                                                    service: service,
                                                    showHistoricalData: showHistoricalData
                                                })
                                            }
                                            className="btn btn-xs btn-outline-secondary btn-cn-edit"
                                        >
                                            Edit
                                        </button>
                                    }
                                    {showHistoricalData &&
                                    <div className="po-date">
                                        {device.startDate && moment(device.startDate).format("MM/DD/YYYY")}
                                        {" - "}
                                        {device.endDate ? moment(device.endDate).format("MM/DD/YYYY") : "Now"}
                                    </div>
                                    }
                                    <div className="container-list-bubble container-list-bubble-equipment" style={{ position: "relative" }}>
                                        {hasDeviceDiagnostics && (() => {
                                            const deviceId = device.id || `${device.deviceProfileId}-${device.serialNumber}`;
                                            const deviceStatus = deviceStatuses[deviceId] || DiagnosticStatus.UNKNOWN;
                                            const deviceLogsData = deviceLogs[deviceId];
                                            const hasLogs = !!deviceLogsData;

                                            return (
                                                <DiagnosticIndicator
                                                    diagnosticResponse={availableDiagnostics}
                                                    statusDiagnostic={deviceStatusDiagnostic}
                                                    isLoading={isLoading}
                                                    onRunAction={this.runDiagnosticAction}
                                                    onToggleLogs={() => this.showLogsModal(device, deviceId)}
                                                    device={device}
                                                    status={deviceStatus}
                                                    logs={deviceLogsData}
                                                    hasLogs={hasLogs}
                                                    style={{
                                                        position: "absolute",
                                                        top: "8px",
                                                        right: "8px"
                                                    }}
                                                />
                                            );
                                        })()}


                                        {device.apiServiceDeviceNums && device.apiServiceDeviceNums.map((num, index) =>
                                          <dl key={index}>
                                              <dt>{num.type}</dt>
                                              <dd>{num.number}</dd>
                                          </dl>
                                        )}
                                    </div>
                                    </div>
                                    );
                                })}

                            </div>
                            }

                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const discountsLoadingSelector = createLoadingSelector(['SET_DISCOUNTS']);

const mapStateToProps = (state) => {
    const canEditEquipment = getUserPermission(state, 'SERVICE_LINES', 'SERVICE_LINE_EDIT_EQUIPMENT');
    const canEditServiceLineNumber = getUserPermission(state, 'SERVICE_LINES', 'SERVICE_LINE_EDIT_SERVICE_LINE_NUMBER');
    const canAddServicelineDiscount = getUserPermission(state, 'ADMN', 'ADMN_ADD_SERVICELINE_DISCOUNT');
    const canEditServicelineDiscount = getUserPermission(state, 'ADMN', 'ADMN_EDIT_SERVICELINE_DISCOUNT');
    const accountId = getAccountDetails(state).accountDetails.accountInfo.id;
    const discountsData = getAccountDetails(state).billing.discounts;
    const discountsLoading = discountsLoadingSelector(state);
    return {
        canEditEquipment,
        canEditServiceLineNumber,
        canAddServicelineDiscount,
        canEditServicelineDiscount,
        accountId,
        discountsData,
        discountsLoading,
    };
};

const mapDispatchToProps = {
    showModal,
    getDiscounts,
};


export default connect(mapStateToProps, mapDispatchToProps)(ServiceSummary);
